import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import ImpersonationBanner from "./components/ImpersonationBanner";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AuthCallback from "./pages/AuthCallback";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import CreateLeague from "./pages/CreateLeague";
import LeaguePayment from "./pages/LeaguePayment";
import PaymentSuccess from "./pages/PaymentSuccess";
import JoinLeague from "./pages/JoinLeague";
import LeagueLayout from "./pages/league/LeagueLayout";
import LeagueOverview from "./pages/league/LeagueOverview";
import LeagueRosa from "./pages/league/LeagueRosa";
import LeagueRinnovi from "./pages/league/LeagueRinnovi";
import LeagueAsta from "./pages/league/LeagueAsta";
import LeagueAICoach from "./pages/league/LeagueAICoach";
import LeagueImport from "./pages/league/LeagueImport";
import LeagueStorico from "./pages/league/LeagueStorico";
import LeagueAdmin from "./pages/league/LeagueAdmin";
import LeagueLog from "./pages/league/LeagueLog";
import LeagueFinanze from "./pages/league/LeagueFinanze";
import LeagueStadio from "./pages/league/LeagueStadio";
import LeagueTrophies from "./pages/league/LeagueTrophies";
import LeagueSponsor from "./pages/league/LeagueSponsor";
import LeagueClassifica from "./pages/league/LeagueClassifica";
import LeagueSponsorHistory from "./pages/league/LeagueSponsorHistory";
import LeagueDecisioniAdmin from "./pages/league/LeagueDecisioniAdmin";
import LeagueDiarioMercato from "./pages/league/LeagueDiarioMercato";
import LeagueMilestones from "./pages/league/LeagueMilestones";
import LeagueSimulate from "./pages/league/LeagueSimulate";
import LeagueSaluteRosa from "./pages/league/LeagueSaluteRosa";
import LeagueGuida from "./pages/league/LeagueGuida";
import StoricoMagnati from "./pages/StoricoMagnati";
import StoricoClub from "./pages/StoricoClub";
import StoricoGiocatore from "./pages/StoricoGiocatore";
import AdminMagnati from "./pages/AdminMagnati";
import RosaSinottica from "./pages/RosaSinottica";
import RankingsMovimenti from "./pages/RankingsMovimenti";
import SuperAdminLogin from "./pages/superadmin/SuperAdminLogin";
import SuperAdminLayout from "./pages/superadmin/SuperAdminLayout";
import SuperAdminOverview from "./pages/superadmin/SuperAdminOverview";
import SuperAdminLeagues from "./pages/superadmin/SuperAdminLeagues";
import SuperAdminUsers from "./pages/superadmin/SuperAdminUsers";
import SuperAdminPayments from "./pages/superadmin/SuperAdminPayments";
import SuperAdminMessages from "./pages/superadmin/SuperAdminMessages";
import SuperAdminHealth from "./pages/superadmin/SuperAdminHealth";
import SuperAdminSponsors from "./pages/superadmin/SuperAdminSponsors";
import SuperAdminRoosterArchive from "./pages/superadmin/SuperAdminRoosterArchive";

import "./App.css";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <div className="font-mono tracking-[0.4em] text-slate-500 uppercase text-sm">Caricamento…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/password-dimenticata" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/lega/nuova" element={<Protected><CreateLeague /></Protected>} />
      <Route path="/lega/unisciti" element={<Protected><JoinLeague /></Protected>} />
      <Route path="/leghe/nuova" element={<Navigate to="/lega/nuova" replace />} />
      <Route path="/leghe/unisciti" element={<Navigate to="/lega/unisciti" replace />} />
      <Route path="/storico-magnati" element={<Protected><StoricoMagnati /></Protected>} />
      <Route path="/storico-magnati/rosa-sinottica" element={<Protected><RosaSinottica /></Protected>} />
      <Route path="/storico-magnati/club/:clubId" element={<Protected><StoricoClub /></Protected>} />
      <Route path="/storico-magnati/giocatore/:playerId" element={<Protected><StoricoGiocatore /></Protected>} />
      <Route path="/admin/magnati" element={<Protected><AdminMagnati /></Protected>} />
      <Route path="/superadmin/login" element={<SuperAdminLogin />} />
      <Route path="/superadmin" element={<SuperAdminLayout />}>
        <Route index element={<SuperAdminOverview />} />
        <Route path="leghe" element={<SuperAdminLeagues />} />
        <Route path="utenti" element={<SuperAdminUsers />} />
        <Route path="pagamenti" element={<SuperAdminPayments />} />
        <Route path="messaggi" element={<SuperAdminMessages />} />
        <Route path="sponsor" element={<SuperAdminSponsors />} />
        <Route path="archivio-quotazioni" element={<SuperAdminRoosterArchive />} />
        <Route path="health" element={<SuperAdminHealth />} />
      </Route>
      <Route path="/pagamento/success" element={<Protected><PaymentSuccess /></Protected>} />
      <Route path="/pagamento/:leagueId" element={<Protected><LeaguePayment /></Protected>} />
      <Route path="/lega/:leagueId" element={<Protected><LeagueLayout /></Protected>}>
        <Route index element={<LeagueOverview />} />
        <Route path="rosa" element={<LeagueRosa />} />
        <Route path="finanze" element={<LeagueFinanze />} />
        <Route path="stadio" element={<LeagueStadio />} />
        <Route path="rinnovi" element={<LeagueRinnovi />} />
        <Route path="asta" element={<LeagueAsta />} />
        <Route path="ai-coach" element={<LeagueAICoach />} />
        <Route path="trofei" element={<Navigate to="../storico" replace />} />
        <Route path="sponsor" element={<LeagueSponsor />} />
        <Route path="sponsor/storico" element={<LeagueSponsorHistory />} />
        <Route path="classifica" element={<LeagueClassifica />} />
        <Route path="decisioni" element={<LeagueDecisioniAdmin />} />
        <Route path="diario-mercato" element={<LeagueDiarioMercato />} />
        <Route path="milestones" element={<LeagueMilestones />} />
        <Route path="salute-rosa" element={<LeagueSaluteRosa />} />
        <Route path="guida" element={<LeagueGuida />} />
        <Route path="sinottica" element={<RosaSinottica />} />
        <Route path="movimenti" element={<RankingsMovimenti />} />
        <Route path="simulate" element={<LeagueSimulate />} />
        <Route path="import" element={<LeagueImport />} />
        <Route path="storico" element={<LeagueStorico />} />
        <Route path="admin" element={<LeagueAdmin />} />
        <Route path="log" element={<LeagueLog />} />
        <Route path="*" element={<Navigate to="" replace />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ImpersonationBanner />
          <AppRouter />
          <Toaster theme="dark" position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
